import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usuariosApi, referenciaApi, zonasApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { ProvinciaSelector } from '../components/ProvinciaSelector';
import ImportadorCSV from '../components/ImportadorCSV';
import {
  Plus, Pencil, Key, PowerOff, Search, Mail, MapPin, ShieldCheck, CircleDot, Upload,
} from 'lucide-react';

const ROL_LABEL = {
  admin: 'Administrador',
  dir_comercial: 'Director Comercial',
  comercial: 'Agente Comercial',
  agente_libre: 'Agente Libre',
  admin_comercial: 'Admin Comercial',
};
const ROL_STYLE = {
  admin: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  dir_comercial: 'bg-blue-50 border-blue-200 text-blue-700',
  comercial: 'bg-brand-50 border-brand-200 text-brand-700',
  agente_libre: 'bg-sky-50 border-sky-200 text-sky-700',
  admin_comercial: 'bg-violet-50 border-violet-200 text-violet-700',
};

const emptyForm = () => ({
  email: '', password: '', nombre: '', apellidos: '',
  rol: 'comercial', provincias_asignadas: [], zona_ids: [],
});

const AdminUsuarios = () => {
  const { user: currentUser } = useAuth();

  const [usuarios, setUsuarios] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: '', rol: '', estado: '' });

  // form dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user or null
  const [form, setForm] = useState(emptyForm());
  const [formErr, setFormErr] = useState('');
  const [saving, setSaving] = useState(false);

  // password reset dialog
  const [pwdUser, setPwdUser] = useState(null);
  const [newPwd, setNewPwd] = useState('');

  // import CSV
  const [importDialog, setImportDialog] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (filters.rol) payload.rol = filters.rol;
      if (filters.estado) payload.estado = filters.estado;
      const [list, provs, zs] = await Promise.all([
        usuariosApi.list(payload),
        referenciaApi.provincias(),
        zonasApi.list().catch(() => []),
      ]);
      setUsuarios(list);
      setProvincias(provs);
      setZonas(zs);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filters.rol, filters.estado]);

  const abrirNuevo = () => { setEditing(null); setForm(emptyForm()); setFormErr(''); setDialogOpen(true); };
  const abrirEditar = (u) => {
    setEditing(u);
    const misZonas = zonas.filter((z) => z.titular_user_id === u.id).map((z) => z.id);
    setForm({
      email: u.email, password: '', nombre: u.nombre, apellidos: u.apellidos || '',
      rol: u.rol,
      provincias_asignadas: u.provincias_manuales || u.provincias_asignadas || [],
      zona_ids: misZonas,
    });
    setFormErr(''); setDialogOpen(true);
  };
  const cerrar = () => { setDialogOpen(false); setEditing(null); setForm(emptyForm()); setFormErr(''); };

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true); setFormErr('');
    try {
      let userId = editing?.id;
      const esComercial = form.rol === 'comercial' || form.rol === 'agente_libre';
      if (editing) {
        await usuariosApi.update(editing.id, {
          nombre: form.nombre,
          apellidos: form.apellidos || null,
          rol: form.rol,
          provincias_asignadas: esComercial ? form.provincias_asignadas : [],
        });
      } else {
        if (form.password.length < 8) throw new Error('La contraseña debe tener al menos 8 caracteres');
        const created = await usuariosApi.create({
          email: form.email,
          password: form.password,
          nombre: form.nombre,
          apellidos: form.apellidos || null,
          rol: form.rol,
          provincias_asignadas: esComercial ? form.provincias_asignadas : [],
        });
        userId = created.id;
      }
      // Sincronizar zonas (admin/dir_comercial también pueden ser titulares si se quisiera)
      if (userId) {
        try {
          await zonasApi.asignarAUsuario(userId, form.zona_ids || []);
        } catch (zerr) {
          // No abortar el flujo — avisar
          console.error('Error sincronizando zonas:', zerr);
          setFormErr(`Usuario guardado, pero falló la asignación de zonas: ${zerr.message}`);
        }
      }
      await cargar();
      if (!formErr) cerrar();
    } catch (err) {
      setFormErr(err.message);
    } finally {
      setSaving(false);
    }
  };

  const desactivar = async (u) => {
    if (!window.confirm(`¿Desactivar a ${u.nombre}? Podrás reactivarlo editándolo más adelante.`)) return;
    try { await usuariosApi.deactivate(u.id); cargar(); } catch (e) { alert(e.message); }
  };
  const reactivar = async (u) => {
    try { await usuariosApi.update(u.id, { estado: 'activo' }); cargar(); } catch (e) { alert(e.message); }
  };

  const aplicarResetPwd = async () => {
    if (newPwd.length < 8) { alert('Mínimo 8 caracteres'); return; }
    try {
      await usuariosApi.resetPassword(pwdUser.id, newPwd);
      setPwdUser(null); setNewPwd('');
    } catch (e) { alert(e.message); }
  };

  const filteredUsers = usuarios.filter((u) => {
    const estadoEfectivo = u.estado || 'activo';
    if (filters.estado && estadoEfectivo !== filters.estado) return false;
    if (!filters.q) return true;
    const qq = filters.q.toLowerCase();
    return (
      u.email.toLowerCase().includes(qq) ||
      u.nombre.toLowerCase().includes(qq) ||
      (u.apellidos || '').toLowerCase().includes(qq)
    );
  });

  return (
    <div data-testid="admin-usuarios">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900">Usuarios</h1>
          <p className="text-ink-500 mt-1">
            Gestiona el equipo comercial, asigna provincias y controla accesos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setImportDialog(true)}
            className="border-brand-200 text-brand-700 hover:bg-brand-50"
            data-testid="btn-importar-usuarios"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Importar CSV
          </Button>
          <Button
            className="bg-brand-500 hover:bg-brand-600 text-white shadow-sm"
            onClick={abrirNuevo}
            data-testid="btn-nuevo-usuario"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nuevo usuario
          </Button>
        </div>
      </div>

      <ImportadorCSV
        open={importDialog}
        onOpenChange={setImportDialog}
        tipo="usuarios"
        titulo="Importar usuarios desde CSV"
        onCompleted={cargar}
      />

      {/* Filters */}
      <Card className="p-4 mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center border-ink-200">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
          <Input
            placeholder="Buscar por email, nombre…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="pl-9 bg-white border-ink-200"
            data-testid="filter-q"
          />
        </div>
        <select
          value={filters.rol}
          onChange={(e) => setFilters({ ...filters, rol: e.target.value })}
          className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
          data-testid="filter-rol"
        >
          <option value="">Todos los roles</option>
          <option value="admin">Administrador</option>
          <option value="dir_comercial">Director Comercial</option>
          <option value="comercial">Agente Comercial</option>
          <option value="agente_libre">Agente Libre</option>
          <option value="admin_comercial">Admin Comercial</option>
        </select>
        <select
          value={filters.estado}
          onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
          className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
          data-testid="filter-estado"
        >
          <option value="">Todos los estados</option>
          <option value="activo">Activos</option>
          <option value="inactivo">Inactivos</option>
        </select>
      </Card>

      {/* Table */}
      <Card className="border-ink-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200 text-ink-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-left font-semibold">Rol</th>
                <th className="px-4 py-3 text-left font-semibold">Zonas / Provincias</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100" data-testid="usuarios-tbody">
              {loading && (
                <tr><td colSpan={5} className="text-center py-12 text-ink-400">Cargando…</td></tr>
              )}
              {!loading && filteredUsers.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-ink-400">Sin resultados</td></tr>
              )}
              {!loading && filteredUsers.map((u) => {
                const esYo = currentUser?.id === u.id;
                const activo = u.estado === 'activo';
                return (
                  <tr key={u.id} className="hover:bg-ink-50" data-testid={`row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-ink-100 flex items-center justify-center text-ink-600 font-semibold text-sm">
                          {u.nombre[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-ink-900 leading-tight">
                            {u.nombre} {u.apellidos || ''}
                            {esYo && <span className="ml-2 text-xs text-ink-400 font-normal">(tú)</span>}
                          </p>
                          <p className="text-xs text-ink-500 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" /> {u.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${ROL_STYLE[u.rol]} border`}>
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        {ROL_LABEL[u.rol] || u.rol}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {(u.rol === 'comercial' || u.rol === 'agente_libre') ? (
                        (() => {
                          const misZonas = zonas.filter((z) => z.titular_user_id === u.id);
                          const cubriendo = zonas.filter((z) => z.cubierto_por_user_id === u.id);
                          const totalProvs = u.provincias_asignadas?.length || 0;
                          if (misZonas.length === 0 && cubriendo.length === 0 && totalProvs === 0) {
                            return (
                              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                                Sin asignar
                              </span>
                            );
                          }
                          return (
                            <div className="flex flex-col gap-1 max-w-xs">
                              {misZonas.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {misZonas.slice(0, 3).map((z) => (
                                    <span key={z.id} className="inline-flex items-center gap-1 text-[10px] bg-brand-50 border border-brand-200 text-brand-700 px-2 py-0.5 rounded" title={z.provincias.join(', ')}>
                                      {z.nombre}
                                    </span>
                                  ))}
                                  {misZonas.length > 3 && <span className="text-[10px] text-ink-500">+{misZonas.length - 3}</span>}
                                </div>
                              )}
                              {cubriendo.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {cubriendo.map((z) => (
                                    <span key={z.id} className="inline-flex items-center gap-1 text-[10px] bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded" title={`Cubriendo temporalmente · ${z.provincias.join(', ')}`}>
                                      ⮕ {z.nombre}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <span className="text-[11px] text-ink-500">
                                <MapPin className="w-3 h-3 inline" /> {totalProvs} provincias efectivas
                              </span>
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-xs text-ink-400">Acceso total</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        className={activo
                          ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                          : 'bg-ink-100 border border-ink-200 text-ink-500'}
                      >
                        <CircleDot className={`w-3 h-3 mr-1 ${activo ? 'text-emerald-500' : ''}`} />
                        {activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => abrirEditar(u)}
                          title="Editar"
                          className="p-2 rounded hover:bg-ink-100 text-ink-600"
                          data-testid={`btn-editar-${u.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setPwdUser(u)}
                          title="Restablecer contraseña"
                          className="p-2 rounded hover:bg-ink-100 text-ink-600"
                          data-testid={`btn-reset-${u.id}`}
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        {activo ? (
                          <button
                            onClick={() => desactivar(u)}
                            disabled={esYo}
                            title={esYo ? 'No puedes desactivarte' : 'Desactivar'}
                            className="p-2 rounded hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid={`btn-desactivar-${u.id}`}
                          >
                            <PowerOff className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => reactivar(u)}
                            title="Reactivar"
                            className="p-2 rounded hover:bg-emerald-50 text-emerald-600"
                            data-testid={`btn-reactivar-${u.id}`}
                          >
                            <CircleDot className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) cerrar(); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-usuario">
          <form onSubmit={guardar}>
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
              <DialogDescription>
                {editing
                  ? 'Actualiza los datos, rol y provincias asignadas.'
                  : 'Crea una cuenta para un miembro del equipo.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-ink-600">Nombre</label>
                  <Input
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    required
                    data-testid="form-nombre"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-ink-600">Apellidos</label>
                  <Input
                    value={form.apellidos}
                    onChange={(e) => setForm({ ...form, apellidos: e.target.value })}
                    data-testid="form-apellidos"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-ink-600">Email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  disabled={!!editing}
                  data-testid="form-email"
                />
              </div>

              {!editing && (
                <div>
                  <label className="text-xs font-medium text-ink-600">Contraseña inicial</label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={8}
                    placeholder="Mínimo 8 caracteres"
                    data-testid="form-password"
                  />
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-ink-600">Rol</label>
                <select
                  value={form.rol}
                  onChange={(e) => setForm({ ...form, rol: e.target.value })}
                  className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-900"
                  data-testid="form-rol"
                >
                  <option value="comercial">Agente Comercial</option>
                  <option value="agente_libre">Agente Libre</option>
                  <option value="admin_comercial">Admin Comercial</option>
                  <option value="dir_comercial">Director Comercial</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {(form.rol === 'comercial' || form.rol === 'agente_libre') && (
                <>
                  <div>
                    <label className="text-xs font-medium text-ink-600 mb-1 block">
                      Zonas asignadas
                      <span className="ml-2 text-ink-400">(serán el titular de las zonas marcadas)</span>
                    </label>
                    <div className="border border-ink-200 rounded-md p-2 bg-white max-h-60 overflow-y-auto" data-testid="user-zonas-selector">
                      {zonas.length === 0 ? (
                        <p className="text-xs text-ink-400 p-2">Aún no hay zonas creadas. Crea zonas en <strong>Zonas</strong> y luego asígnalas aquí.</p>
                      ) : zonas.map((z) => {
                        const otroTitular = z.titular_user_id && editing && z.titular_user_id !== editing.id;
                        const checked = (form.zona_ids || []).includes(z.id);
                        return (
                          <label key={z.id} className="flex items-start gap-2 py-1 px-1 hover:bg-ink-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              className="accent-brand-500 mt-0.5"
                              checked={checked}
                              onChange={(e) => {
                                const set = new Set(form.zona_ids || []);
                                if (e.target.checked) set.add(z.id); else set.delete(z.id);
                                setForm({ ...form, zona_ids: [...set] });
                              }}
                              data-testid={`user-zona-${z.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-ink-900">
                                {z.nombre}
                                <span className="text-ink-400 text-xs ml-1">· {z.provincias.length} provincias</span>
                                {otroTitular && !checked && (
                                  <span className="ml-2 text-[10px] bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 rounded">
                                    Actualmente: {z.titular_nombre}
                                  </span>
                                )}
                                {z.cubierto_por_user_id && (
                                  <span className="ml-2 text-[10px] bg-ink-100 border border-ink-200 text-ink-600 px-1.5 py-0.5 rounded">
                                    Cubierta por {z.cubierto_por_nombre}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-ink-500 truncate">
                                {z.provincias.slice(0, 5).join(', ')}{z.provincias.length > 5 ? '…' : ''}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {(() => {
                      const provsDeZonasSeleccionadas = new Set();
                      (form.zona_ids || []).forEach((zid) => {
                        const z = zonas.find((x) => x.id === zid);
                        if (z) z.provincias.forEach((p) => provsDeZonasSeleccionadas.add(p));
                      });
                      return provsDeZonasSeleccionadas.size > 0 ? (
                        <p className="text-[11px] text-ink-500 mt-1">
                          Total provincias cubiertas por estas zonas: <strong>{provsDeZonasSeleccionadas.size}</strong>
                        </p>
                      ) : null;
                    })()}
                  </div>

                  <details>
                    <summary className="text-xs font-medium text-ink-500 cursor-pointer hover:text-ink-700">
                      Provincias sueltas (avanzado — fuera de cualquier zona)
                    </summary>
                    <div className="mt-2">
                      <ProvinciaSelector
                        provincias={provincias}
                        selected={form.provincias_asignadas}
                        onChange={(sel) => setForm({ ...form, provincias_asignadas: sel })}
                      />
                      <p className="text-[11px] text-ink-400 mt-1">
                        Uso típico: un comercial que además cubre una provincia puntual sin formar parte de una zona formal.
                      </p>
                    </div>
                  </details>
                </>
              )}

              {formErr && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="form-error">
                  {formErr}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={cerrar}>Cancelar</Button>
              <Button
                type="submit"
                className="bg-brand-500 hover:bg-brand-600 text-white"
                disabled={saving}
                data-testid="btn-guardar-usuario"
              >
                {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear usuario'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!pwdUser} onOpenChange={(o) => { if (!o) { setPwdUser(null); setNewPwd(''); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-reset-pwd">
          <DialogHeader>
            <DialogTitle>Restablecer contraseña</DialogTitle>
            <DialogDescription>
              Nueva contraseña para <span className="font-semibold">{pwdUser?.email}</span>.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            placeholder="Mínimo 8 caracteres"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            minLength={8}
            data-testid="input-new-password"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwdUser(null); setNewPwd(''); }}>Cancelar</Button>
            <Button
              className="bg-brand-500 hover:bg-brand-600 text-white"
              onClick={aplicarResetPwd}
              data-testid="btn-aplicar-reset"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsuarios;
